import { BaseService } from "./BaseService";

export interface GeneratedTokenResponseDto {
  chatUserId: string;
  token: string;
  name: string;
}

export interface CreateChannelDto {
  bookingId: string;
}

export interface CreateInternalChannelDto {
  targetUserIds: string[];
  chat_type: "private" | "group";
  name?: string;
}

export interface UpdateInternalChannelDto {
  targetUserIds: string[];
  name?: string;
}

export interface ChannelUser {
  id: string;
  image?: string;
  last_active?: string;
  name?: string;
  username?: string;
  online?: boolean;
  shadow_banned?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ChannelMember {
  user_id?: string;
  user?: ChannelUser;
  updated_at?: string;
}

export interface ChannelResponseDto {
  id?: string;
  type: string;
  created_by?: ChannelUser | null;
  created_by_id?: string;
  member_count?: number;
  members?: ChannelMember[];
  message_count?: number;
  updated_at?: string;
}

export interface CreateChatNoteDto {
  content: string;
}

export interface ChatNoteResponseDto {
  id: string;
  chatId: string;
  content: string;
  authorId: string;
  createdAt: string;
  updatedAt: string;
}

class ChatService extends BaseService {
  private basePath = "/chat";

  constructor() {
    super();
  }
  async createChannel(dto: CreateChannelDto) {
    return this.post<ChannelResponseDto>(
      `${this.basePath}/booking-channel`,
      dto,
    );
  }

  async createInternalChannel(dto: CreateInternalChannelDto) {
    return this.post<ChannelResponseDto>(
      `${this.basePath}/internal-channel`,
      dto,
    );
  }

  async updateInternalChannel(id: string, dto: UpdateInternalChannelDto) {
    return this.patch<ChannelResponseDto>(
      `${this.basePath}/internal-channel/${id}`,
      dto,
    );
  }

  async deleteInternalChannel(id: string) {
    return this.delete<void>(`${this.basePath}/internal-channel/${id}`);
  }

  async getHotelUserChatToken(): Promise<GeneratedTokenResponseDto> {
    return this.post<GeneratedTokenResponseDto>(`${this.basePath}/hotel-token`);
  }

  async getInternalUserChatToken(): Promise<GeneratedTokenResponseDto> {
    return this.post<GeneratedTokenResponseDto>(
      `${this.basePath}/internal-token`,
    );
  }

  async translateMessage(
    messageId: string,
    targetLanguage?: string,
    translateTranscription = false,
  ) {
    return this.post<string>(
      `${this.basePath}/messages/${messageId}/translate`,
      {
        ...(targetLanguage ? { targetLanguage } : {}),
        translateTranscription,
      },
    );
  }

  async transcribeMessage(messageId: string) {
    return this.post<string>(
      `${this.basePath}/messages/${messageId}/transcribe`,
      {},
    );
  }

  async getSuggestResponse(channelId: string) {
    return this.get<{ text: string }>(`${this.basePath}/${channelId}/suggest`);
  }

  async getChatNotes(channelId: string) {
    return this.get<ChatNoteResponseDto[]>(
      `${this.basePath}/${channelId}/notes`,
    );
  }

  async createChatNote(channelId: string, dto: CreateChatNoteDto) {
    return this.post<ChatNoteResponseDto>(
      `${this.basePath}/${channelId}/notes`,
      dto,
    );
  }

  async deleteChatNote(channelId: string, noteId: string) {
    return this.delete<void>(`${this.basePath}/${channelId}/notes/${noteId}`);
  }
}

export const chatService = new ChatService();
